// WebSocket — tech/03 §19. Прототип: рукопожатие, пульс, сессия, кошелёк, платёж, HP босса.
// Остальные типы §19 (кооператив, чат, события) добавляются задачами своих этапов.
import { ChatMessage } from './api/chat.js';
import { z } from 'zod';
import { EpochMs, Grant, PublicId, Uuid7, strict } from './common.js';
import { Wallet } from './api/models.js';

export const WS_PATH = '/api/v1/ws';
export const WS_PING_INTERVAL_MS = 25_000;
export const WS_PONG_TIMEOUT_MS = 60_000;
export const WS_AUTH_TIMEOUT_MS = 5_000;
/** Задержки переподключения, ±30% (03 §19). */
export const WS_RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 15000] as const;

/** Сообщения клиента. */
export const WsClientMessage = z.discriminatedUnion('t', [
  strict({ t: z.literal('auth'), token: z.string().min(16) }),
  strict({ t: z.literal('sub'), topics: z.array(z.string().max(64)).max(16) }),
  strict({ t: z.literal('unsub'), topics: z.array(z.string().max(64)).max(16) }),
  strict({ t: z.literal('pong') }),
]);
export type WsClientMessage = z.infer<typeof WsClientMessage>;

/** Служебные сообщения сервера без конверта. */
export const WsControl = z.discriminatedUnion('t', [
  strict({ t: z.literal('ready'), sid: z.string(), serverTime: EpochMs }),
  strict({ t: z.literal('reauth') }),
  strict({ t: z.literal('ping') }),
  /** Подписка отвергнута: нет права на тему (03 §19 «Сервер проверяет право на тему», E01-T05). Решение Р-46. */
  strict({ t: z.literal('denied'), topics: z.array(z.string().max(64)) }),
]);

const env = <T extends string, D extends z.ZodType>(t: T, d: D) =>
  strict({ t: z.literal(t), seq: z.int().nonnegative(), ts: EpochMs, d });

/** Конверт сервера `{t, seq, ts, d}` (03 §19). */
export const WsEvent = z.discriminatedUnion('t', [
  env('boss.hp', strict({ iid: Uuid7, hp: z.int().nonnegative(), hpMax: z.int().positive(), top3: z.array(z.unknown()).optional() })),
  env('boss.defeated', strict({ iid: Uuid7, killer: strict({ publicId: PublicId, name: z.string() }), at: z.iso.datetime() })),
  env('wallet.updated', strict({ wallet: Wallet })),
  env('payment.credited', strict({ orderId: Uuid7, grants: Grant, wallet: Wallet })),
  env('payment.refunded', strict({ orderId: Uuid7, clawback: Grant })),
  env('mail.new', strict({ count: z.int().nonnegative() })),
  env('chat.message', ChatMessage),
  env('chat.hidden', strict({ channel: z.string(), id: z.string(), reason: z.enum(['filter', 'moderator', 'author']) })),
  env('session.revoked', strict({ reason: z.enum(['elsewhere', 'logout', 'ban']), sid: z.string().optional() })),
  env('notify', strict({ kind: z.string(), titleKey: z.string(), params: z.record(z.string(), z.unknown()) })),
  env('config.client', strict({ version: z.int().nonnegative() })),
  env('content.release', strict({ release: z.string(), manifestUrl: z.url(), sha256: z.string(), mandatory: z.boolean() })),
  env('maintenance', strict({ from: z.iso.datetime(), to: z.iso.datetime() })),
]);
export type WsEvent = z.infer<typeof WsEvent>;
export const WsServerMessage = z.union([WsControl, WsEvent]);
